import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import LdsButton from "../../components/LdsButton/index.jsx";
import LdsImage from "../../components/LdsImage/index.jsx";
import NegativeScan from "../../assets/module-5/negative-transverse.png";
import PositiveScan from "../../assets/module-5/positive-transverse.png";
import { FigureCaption } from "react-bootstrap";
import LdsTile from "../../components/LdsTile/index.jsx";
import References from "../../components/References/index.jsx";

import { SideBar } from "../../components/SideBar";
import { useSelector } from "react-redux";

import { ReactLoaderSpinner } from "../../components/ReactLoaderSpinner/index.js";
// services
import { useBookmarkModuleMutation } from "../../store/services/moduleServerApi";

const Module_5_0_5 = () => {
  const { pathname: pathName } = useLocation();
  const { menuItems } = useSelector((state) => state?.moduleSlice);

  const [bookmarkModule, { isLoading: isMenuLoading }] =
    useBookmarkModuleMutation();

  /* Start useEffect */
  useEffect(() => {
    bookmarkModule({ pathName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // * onPageLoad
  /* End useEffect */

  return (
    <div className="row">
      {isMenuLoading && <ReactLoaderSpinner />}
      {menuItems?.id && <SideBar menuItems={[menuItems]} />}

      <div className="col-sm-8">
        <main className="module-content">
          <section className="mb-5 learning-heading">
            <h1 className="blue text-uppercase b mt-5">
              5.0.5 For Referring Physicians: Key Report Components
              <sup>1</sup>
            </h1>
            <p className="mt-3 mb-0">Estimated time to complete: 3 minutes</p>
          </section>
          <section>
            <div className="content-block mb-5 mt-3">
              <p>
                This section provides training on key imaging report components,
                specific to referring physicians. There are 4 recommended
                components in the report based on the Society of Nuclear
                Medicine and Molecular Imaging (SNMMI) and European Association
                of Nuclear Medicine (EANM) guidelines for amyloid PET imaging of
                the brain.<sup>1</sup>
              </p>
            </div>
            <div className="content-container-sm">
              <div className="content-container blue-rounded-border mt-4">
                <h2 className="h3 b text-center mt-3 mb-3">Amyvid Scans</h2>
                <div className="row">
                  <div className="col-sm-12 col-md-6 p-4">
                    <LdsImage
                      src={NegativeScan}
                      alt="Two brain scan images labeled 'Amyvid Scans.' The left scan is labeled 'Negative,' showing a lighter and less defined image, while the right scan is labeled 'Positive,' displaying a darker, more defined image"
                      style={{ maxWidth: "250px" }}
                      className=""
                    />
                  </div>
                  <div className="col-sm-12 col-md-6 p-4">
                    <LdsImage
                      src={PositiveScan}
                      alt="Two brain scan images labeled 'Amyvid Scans.' The left scan is labeled 'Negative,' showing a lighter and less defined image, while the right scan is labeled 'Positive,' displaying a darker, more defined image”"
                      style={{ maxWidth: "250px" }}
                      className=""
                    />
                  </div>
                </div>
              </div>
              <FigureCaption className="mt-3 mb-5">
                <p className="fz-sm">
                  ©2016. Avid Radiopharmaceuticals, Inc. All rights reserved.
                </p>
                <p className="fz-sm">
                  Data on File, Lilly Research Laboratories: AMY20121001A,
                  AMY20121001B.
                </p>
              </FigureCaption>
            </div>
            <div className="content-block mb-5 mt-3">
              <p className="h6 bold">
                The amyloid PET scan report should include the components of
                indication, technique, findings, and impression.<sup>1</sup>
              </p>
              <p className="h6 bold">
                Impression will indicate negative or positive*
              </p>
              <ul className="pl-0">
                <li>
                  Images are designated as positive or negative by comparing the
                  radioactivity in cortical gray matter with activity in the
                  adjacent white matter; this determination is made only in the
                  cerebral cortex<sup>2</sup>
                </li>
                <ul className="pl-0">
                  <li>
                    The signal uptake in the cerebellum does not contribute to
                    the scan interpretation (for example, a positive scan may
                    show retained cerebellar gray-white contrast even when the
                    cortical gray-white contrast is lost)
                  </li>
                </ul>
              </ul>
              <LdsTile className="orange-border-tile mt-4 mb-4">
                <p className="bold">
                  Negative scans: Sparse to no neuritic plaques
                </p>
              </LdsTile>
              <LdsTile className="orange-border-tile mt-4 mb-4">
                <p className="bold">
                  Positive scans: Moderate to frequent Aβ deposition
                </p>
              </LdsTile>
            </div>
            <div className="content-block callout mb-5 mt-3">
              <h2 className="h5 bold">Additional support to interpret scans</h2>
              <ul className="pl-0 mt-3">
                <li>
                  If there was uncertainty as to the location or edge of gray
                  matter on the PET scan and a co-registered CT image is
                  available (as when the study was done on a PET/CT scanner) the
                  interpreter should examine the CT and/or fused images and
                  clarify to which tissue the radiotracer uptake localizes
                </li>
                <li>
                  Refer to the challenge cases in Module 4 in the Amyvid Online
                  Reader Training site for more practice with these types of
                  common challenges
                </li>
                <li>
                  Amyloid PET scan interpretation by the radiologist should be
                  done independent of clinical information
                </li>
              </ul>
              <h2 className="h5 bold mt-3">How to assess the report</h2>
              <ul className="pl-0 mt-3">
                <li>
                  The final findings reported by the referring physician to the
                  patient should integrate scan findings along with clinical
                  information and suggest appropriate diagnosis and differential
                  diagnosis, and management of the patient as appropriate
                </li>
              </ul>
            </div>
          </section>
          <section className="button-control">
            <LdsButton
              classes="white-button mt-3 mb-5 prev"
              onClick={function navClick() {
                window.open("/module-5/module-5-0-4", "_self");
              }}
            >
              Previous Lesson
            </LdsButton>
            <LdsButton
              classes="blue-button mt-3 mb-5 align-self-right float-end next"
              onClick={function navClick() {
                window.open("/module-5/module-5-1-0", "_self");
              }}
            >
              Next Lesson
            </LdsButton>
          </section>
          <References>
            <ol className="no-indent fz-sm">
              <li>
                Minoshima S, Drzezga AE, Barthel H, et al. SNMMI Procedure
                Standard/EANM Practice Guideline for Amyloid PET Imaging of the
                Brain 1.0. <em>J Nucl Med.</em> 2016;57(8):1316-1322.
              </li>
              <li>
                Amyvid (florbetapir F 18 injection) Prescribing Information.
                Lilly USA, LLC.{" "}
              </li>
            </ol>
          </References>
        </main>
      </div>
    </div>
  );
};

export default Module_5_0_5;
